import { NetworkStatus, useQuery } from '@apollo/client';
import { Box, CircularProgress } from '@material-ui/core';
import React, { useState } from 'react';
import {
  FETCH_RELATED_BIBS_COUNT,
  getRelatedBibsQuery,
} from '../../../../apollo/queries/bib';
import styles from '../../../HoldingsTable/HoldingsTable.module.scss';
import searchStyles from '../../../../pages/search.module.scss';
import ListElement from '../../../ListElement/ListElement';
import ConnectedRecordsToggler from './ConnectedRecordToggler/ConnectedRecordsToggler';
import ViewMyReservationModal from '../ViewMyReservationModal/ViewMyReservationModal';
import ViewMyRequestModal from '../ViewMyRequestModal/ViewMyRequestModal';
import { useStaticQuery, graphql } from 'gatsby';
import jwt_decode from 'jwt-decode';
import { documentTypes } from '../../../../constants/documentTypes';
import { getValueFromMarc } from '../../../../utils/marc';
import { controlledFeatures } from '../../../../utils/featureControl';

const DOC_TYPE_QUERY = graphql`
  query GetDoctypeDataV2 {
    allDoctypeMappingCsv {
      nodes {
        hun
        key
        svg
        eng
      }
    }
  }
`;

const fetchAtOnce = 5;

const ConnectedRecords = ({
  connectedBibIds = [],
  me,
  parentGuids = [],
  periodicalLevels = [],
  name = '',
  periodicalType,
}) => {
  const [showTable, setShowTable] = useState(false);

  const accessToken =
    typeof window !== 'undefined' && window.sessionStorage
      ? sessionStorage.getItem('accessToken')
      : '';

  const docTypeData = useStaticQuery(DOC_TYPE_QUERY);
  const { allDoctypeMappingCsv } = docTypeData;

  const { data, loading, fetchMore, networkStatus } = useQuery(
    getRelatedBibsQuery({fetchItems: controlledFeatures.itemList}),
    {
      variables: {
        where: {
          id_in: connectedBibIds,
          ...(periodicalLevels.length > 0
            ? { periodicalLevel_in: periodicalLevels }
            : null),
        },
        ...(accessToken
          ? {
              calculatedItemPolicyForPatronWhere: {
                id: jwt_decode(accessToken).guid,
              },
            }
          : null),
        first: fetchAtOnce,
      },
      skip: !showTable || !connectedBibIds,
      notifyOnNetworkStatusChange: true,
    }
  );

  const { data: countData, loading: countLoading } = useQuery(
    FETCH_RELATED_BIBS_COUNT,
    {
      variables: {
        where: {
          id_in: connectedBibIds,
          ...(periodicalLevels.length > 0
            ? { periodicalLevel_in: periodicalLevels }
            : null),
        },
        ...(accessToken
          ? {
              calculatedItemPolicyForPatronWhere: {
                id: jwt_decode(accessToken).guid,
              },
            }
          : null),
      },
      skip: !connectedBibIds,
    }
  );

  const [reservationData, setReservationData] = useState(null);
  const [requestData, setRequestData] = useState(null);

  // nincs a példányhoz rekord kapcsolat vagy
  if (
    connectedBibIds.length === 0 ||
    countData?.searchBibliographicRecord?.aggregate?.count === 0 ||
    countLoading
  )
    return null;

  const loadMore = () => {
    fetchMore({
      variables: {
        where: {
          id_in: connectedBibIds,
          ...(periodicalLevels.length > 0
            ? { periodicalLevel_in: periodicalLevels }
            : null),
        },
        ...(accessToken
          ? {
              calculatedItemPolicyForPatronWhere: {
                id: jwt_decode(accessToken).guid,
              },
            }
          : null),
        first: fetchAtOnce,
        skip: data?.searchBibliographicRecord?.nodes?.length,
      },
      updateQuery(prev, { fetchMoreResult }) {
        if (!fetchMoreResult) return prev;

        return {
          ...prev,
          searchBibliographicRecord: {
            ...prev?.searchBibliographicRecord,
            nodes: [
              ...prev?.searchBibliographicRecord?.nodes,
              ...fetchMoreResult?.searchBibliographicRecord?.nodes,
            ],
          },
        };
      },
    });
  };
  const get901bValue = record => {
    const subfieldValue = record.rawMarcRecord.datafields
      .find(field => field.tag === '901')
      ?.subfields?.find(subfield => subfield.code === 'b')?.value?.value;
    return (
      documentTypes.find(type => type.value === subfieldValue)?.displayName ||
      subfieldValue
    );
  };

  return (
    <Box
      className={[styles.tableContainer, 'tableContainer'].join(' ')}
      style={{ border: '1px solid #e8b96b' }}
    >
      <ConnectedRecordsToggler
        showTable={showTable}
        setShowTable={setShowTable}
        name={name}
      />

      {loading && networkStatus === NetworkStatus.loading && (
        <div style={{ textAlign: 'center' }}>
          <CircularProgress />
        </div>
      )}

      {/* !loading && */ data?.searchBibliographicRecord?.nodes?.map(record => (
        <li key={record.guid} className={searchStyles.searchSingleItem}>
          <ListElement
            // selectedRecords={selectedRecords}
            allDoctypeMappingCsv={allDoctypeMappingCsv}
            node={{
              guid: record.guid,
              type: getValueFromMarc('901', 'b', record.rawMarcRecord),
              title:
                getValueFromMarc('787', 'g', record?.rawMarcRecord) ||
                getValueFromMarc('774', 'g', record?.rawMarcRecord) ||
                getValueFromMarc('245', 'a', record?.rawMarcRecord),
              author: record?.author,
              placeOfAppearance: record?.publicationPlace,
              publisher: record?.publisher,
              releaseDate: record?.releaseDate,
              rawMarcRecord: record?.rawMarcRecord,
              items: record?.physicalManifestations?.nodes,
              itemCount: record.physicalManifestations?.aggregate?.count,
              publicFiles: record.publicFiles,
            }}
            me={me}
            setReservationData={setReservationData}
            setRequestData={setRequestData}
            parentGuids={[...parentGuids, record.guid]}
            periodicalType={periodicalType}
          />
        </li>
      ))}
      {data?.searchBibliographicRecord?.nodes?.length <
        countData?.searchBibliographicRecord?.aggregate?.count && (
        <p className={styles.displayMoreButton} onClick={loadMore}>
          {networkStatus === NetworkStatus.fetchMore && (
            <CircularProgress size={15} />
          )}{' '}
          + Mutass többet
        </p>
      )}
      <ViewMyReservationModal
        reservationData={reservationData}
        handleClose={() => setReservationData(null)}
      />
      <ViewMyRequestModal
        requestData={requestData}
        handleClose={() => setRequestData(null)}
      />
    </Box>
  );
};

export default ConnectedRecords;
